import React, { useEffect, useState } from "react";
import {
  Container,
  CircularProgress,
  Alert,
  TextField,
  Box,
  Typography,
  ThemeProvider,
  CssBaseline,
  InputAdornment,
  Button,
  Divider,
  Grid,
  Paper,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import axios from "axios";
import { SERVER } from "../../../../Constants";
import ImageLoader from "../ImageLoader";
import ViewUserComponent from "./ViewUserComponent";
import darkTheme from "../dashboardTheme";

const getUsers = async (search = null) => {
  const apiClient = axios.create({
    baseURL: SERVER,
    withCredentials: true,
  });

  const params = {};
  if (search) params.search = search;

  const response = await apiClient.get("user/getUsers", { params });
  return response.data;
};

const calculateAge = (dobString) => {
  if (!dobString) return "N/A";
  const dob = new Date(dobString);
  if (isNaN(dob.getTime())) return "N/A"; // invalid date fallback

  const diff = Date.now() - dob.getTime();
  const ageDate = new Date(diff);
  return Math.abs(ageDate.getUTCFullYear() - 1970);
};

const AllUsers = () => {
  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);

  const fetchUsers = async (searchValue = "") => {
    setLoading(true);
    setError(null);
    try {
      const result = await getUsers(searchValue);
      setUsers(result);
    } catch {
      setError("Failed to fetch users");
    } finally {
      setLoading(false);
    }
  };

  // Debounced search
  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      fetchUsers(search);
    }, 400);
    return () => clearTimeout(delayDebounce);
  }, [search]);

  useEffect(() => {
    fetchUsers();
  }, []);

  console.log(users);
  if (selectedUser) {
    return (
      <ViewUserComponent
        user={selectedUser}
        backHandler={() => {
          setSelectedUser(null);
          fetchUsers(search);
        }}
      />
    );
  }

  return (
    <ThemeProvider theme={darkTheme}>
      <CssBaseline />

      <Box
        sx={{
          minHeight: "100vh",
          backgroundColor: "background.default",
          py: 5,
        }}
      >
        <Container maxWidth="xl">
          {/* Header */}
          <Typography variant="h4" fontWeight="bold" mb={3} letterSpacing={0.3}>
            Find Users
          </Typography>

          {/* Search */}
          <TextField
            placeholder="Search by name, username or email"
            variant="outlined"
            fullWidth
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{
              mb: 4,
              backgroundColor: "background.paper",
              borderRadius: 2,
              "& .MuiOutlinedInput-root": {
                borderRadius: 2,
                "& fieldset": { borderColor: "rgba(255,255,255,0.1)" },
                "&:hover fieldset": {
                  borderColor: "rgba(199, 164, 93, 0.5)",
                },
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: "text.secondary" }} />
                </InputAdornment>
              ),
            }}
          />

          {/* Loading / Error */}
          {loading && (
            <Box sx={{ textAlign: "center", py: 5 }}>
              <CircularProgress color="primary" />
            </Box>
          )}
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {/* Grid of User Cards */}
          <Grid container spacing={3}>
            {users.map((user) => {
              const profile = user.profile || {};
              const imageUrl =
                profile.profilePicture?.url ||
                profile.photos?.[0]?.url ||
                "https://via.placeholder.com/100";

              return (
                <Grid item xs={12} sm={12} md={4} key={user._id}>
                  <Paper
                    elevation={3}
                    sx={{
                      p: 3,
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      textAlign: "center",
                      borderRadius: 3,
                      backgroundColor: "background.paper",
                      border: "1px solid rgba(255,255,255,0.05)",
                      transition: "all 0.3s ease",
                      "&:hover": {
                        transform: "translateY(-4px)",
                        boxShadow: "0 8px 20px rgba(199, 164, 93, 0.25)",
                        borderColor: "rgba(199, 164, 93, 0.4)",
                      },
                    }}
                  >
                    <ImageLoader
                      source={{ uri: imageUrl }}
                      alt={user.fullname || user.username}
                      style={{
                        width: 90,
                        height: 90,
                        borderRadius: "50%",
                        objectFit: "cover",
                        objectPosition: "top", // <-- centers face/head
                        marginBottom: 16,
                        border: "2px solid rgba(199, 164, 93, 0.6)",
                      }}
                    />
                    <Typography
                      variant="h6"
                      fontWeight="600"
                      py={1}
                      px={0}
                      m={0}
                    >
                      {user.fullname}
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      py={1}
                      px={0}
                      m={0}
                    >
                      @{user.username} · {user.email}
                    </Typography>

                    <Divider
                      sx={{
                        my: 2,
                        width: "60%",
                        borderColor: "rgba(199, 164, 93, 0.2)",
                      }}
                    />

                    <Typography
                      variant="body2"
                      color="text.secondary"
                      py={1}
                      px={0}
                      m={0}
                    >
                      Role: {user.role} | Tier: {user.tier}
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      py={1}
                      px={0}
                      m={0}
                    >
                      Age: {calculateAge(profile.meta?.dob)} · Location:{" "}
                      {profile.meta?.city || "N/A"},{" "}
                      {profile.meta?.country?.name || "N/A"}
                    </Typography>

                    <Button
                      variant="contained"
                      sx={{
                        mt: 2.5,
                        px: 3,
                        py: 1,
                        borderRadius: 2,
                        fontWeight: "600",
                        textTransform: "none",
                        backgroundColor: "#c7a45d",
                        color: "#0d0d0d",
                        "&:hover": {
                          backgroundColor: "#a6d4fa",
                          boxShadow: "0 0 12px rgba(199, 164, 93, 0.4)",
                        },
                      }}
                      onClick={() => setSelectedUser(user)}
                    >
                      View Profile
                    </Button>
                  </Paper>
                </Grid>
              );
            })}
          </Grid>
        </Container>
      </Box>
    </ThemeProvider>
  );
};

export default AllUsers;
